import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Download, 
  FileText, 
  Settings, 
  Globe, 
  Users, 
  MapPin,
  CheckCircle,
  AlertTriangle,
  Printer,
  Smartphone,
  Book,
  CreditCard,
  Home,
  Zap
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import toast from 'react-hot-toast';
import { GuideType, SafetyProtocol } from '../../types/emergency';
import { GUIDE_TEMPLATES, SAFETY_PROTOCOLS } from '../../data/emergencyData';

interface DownloadGuidesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CustomizationOptions {
  familyMembers: string[];
  location: string;
  selectedDisasters: string[];
  language: string;
  includeContacts: boolean;
  includeLocalInfo: boolean;
  format: 'comprehensive' | 'quick-reference' | 'checklist';
}

const DownloadGuidesModal: React.FC<DownloadGuidesModalProps> = ({ isOpen, onClose }) => {
  const [selectedGuide, setSelectedGuide] = useState<GuideType | null>(null);
  const [showCustomization, setShowCustomization] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [customization, setCustomization] = useState<CustomizationOptions>({
    familyMembers: [''],
    location: '',
    selectedDisasters: [],
    language: 'en',
    includeContacts: true,
    includeLocalInfo: true,
    format: 'comprehensive'
  });

  const disasterTypes = [
    'earthquake', 'wildfire', 'flood', 'hurricane', 'tornado', 'fire'
  ];

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' },
    { code: 'fr', name: 'Français' }
  ];

  const addFamilyMember = () => {
    setCustomization({
      ...customization,
      familyMembers: [...customization.familyMembers, '']
    });
  };

  const updateFamilyMember = (index: number, value: string) => {
    const updated = [...customization.familyMembers];
    updated[index] = value;
    setCustomization({ ...customization, familyMembers: updated });
  };

  const removeFamilyMember = (index: number) => {
    const updated = customization.familyMembers.filter((_, i) => i !== index);
    setCustomization({ ...customization, familyMembers: updated });
  };

  const generatePDF = async (guide: GuideType) => {
    setIsGenerating(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      let yPosition = margin;

      // Helper function to add new page if needed
      const checkPageBreak = (requiredHeight: number) => {
        if (yPosition + requiredHeight > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }
      };

      // Title Page
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text(guide.title, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Generated by DisasterConnect Emergency System', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 20;

      // Customization Info
      if (customization.location) {
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Location-Specific Information', margin, yPosition);
        yPosition += 8;
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Location: ${customization.location}`, margin, yPosition);
        yPosition += 15;
      }

      // Family Members
      if (customization.familyMembers.filter(m => m.trim()).length > 0) {
        checkPageBreak(30);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Family Emergency Plan', margin, yPosition);
        yPosition += 8;
        
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        customization.familyMembers.filter(m => m.trim()).forEach((member, index) => {
          pdf.text(`${index + 1}. ${member}`, margin + 5, yPosition);
          yPosition += 6;
        });
        yPosition += 10;
      }

      // Emergency Contacts Section
      if (customization.includeContacts) {
        checkPageBreak(50);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('EMERGENCY CONTACTS', margin, yPosition);
        yPosition += 10;

        const emergencyContacts = [
          { name: '911 Emergency Services', number: '911', desc: 'Police, Fire, Medical Emergency' },
          { name: 'Poison Control', number: '1-800-222-1222', desc: '24/7 Poison Emergency Hotline' },
          { name: 'Crisis Text Line', number: '741741', desc: 'Text HOME for mental health crisis' },
          { name: 'FEMA Disaster Assistance', number: '1-800-621-3362', desc: 'Federal disaster relief' }
        ];

        emergencyContacts.forEach(contact => {
          checkPageBreak(15);
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.text(contact.name, margin, yPosition);
          yPosition += 6;
          pdf.setFont('helvetica', 'normal');
          pdf.text(`Phone: ${contact.number}`, margin + 5, yPosition);
          yPosition += 5;
          pdf.setFontSize(10);
          pdf.text(contact.desc, margin + 5, yPosition);
          yPosition += 10;
        });
      }

      // Safety Protocols
      const relevantProtocols = guide.content.filter(protocol => 
        customization.selectedDisasters.length === 0 || 
        customization.selectedDisasters.includes(protocol.type)
      );

      relevantProtocols.forEach((protocol, index) => {
        checkPageBreak(40);
        
        // Protocol Title
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${index + 1}. ${protocol.title}`, margin, yPosition);
        yPosition += 8;

        // Description
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        const descLines = pdf.splitTextToSize(protocol.description, pageWidth - 2 * margin);
        pdf.text(descLines, margin, yPosition);
        yPosition += descLines.length * 5 + 5;

        // Time estimate
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'italic');
        pdf.text(`Estimated Time: ${protocol.estimatedTime}`, margin, yPosition);
        yPosition += 8;

        // Steps
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Steps:', margin, yPosition);
        yPosition += 6;

        protocol.steps.forEach((step, stepIndex) => {
          checkPageBreak(8);
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          const stepText = `${stepIndex + 1}. ${step}`;
          const stepLines = pdf.splitTextToSize(stepText, pageWidth - 2 * margin - 10);
          pdf.text(stepLines, margin + 5, yPosition);
          yPosition += stepLines.length * 4 + 2;
        });
        
        yPosition += 10;
      });

      // Emergency Kit Checklist
      if (customization.format === 'comprehensive' || customization.format === 'checklist') {
        checkPageBreak(60);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('EMERGENCY KIT CHECKLIST', margin, yPosition);
        yPosition += 10;

        const kitItems = [
          'Water (1 gallon per person per day for 3 days)',
          'Non-perishable food (3-day supply)',
          'Battery-powered or hand crank radio',
          'Flashlight and extra batteries',
          'First aid kit',
          'Whistle for signaling help',
          'Dust masks and plastic sheeting',
          'Moist towelettes and garbage bags',
          'Wrench or pliers to turn off utilities',
          'Manual can opener',
          'Local maps',
          'Cell phone with chargers',
          'Cash in small bills',
          'Emergency contact information',
          'Copies of important documents',
          'Prescription medications',
          'Infant formula and diapers (if needed)',
          'Pet food and supplies (if needed)',
          'Change of clothing and sturdy shoes',
          'Fire extinguisher',
          'Matches in waterproof container'
        ];

        kitItems.forEach(item => {
          checkPageBreak(6);
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          pdf.text(`☐ ${item}`, margin, yPosition);
          yPosition += 5;
        });
      }

      // Footer on each page
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        pdf.text('DisasterConnect Emergency System', margin, pageHeight - 10);
      }

      // Save the PDF
      const fileName = `${guide.title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      toast.success('Guide downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const getGuideIcon = (type: string) => {
    switch (type) {
      case 'general': return Book;
      case 'quick-reference': return CreditCard;
      case 'custom': return Settings;
      case 'disaster-specific': return AlertTriangle;
      default: return FileText;
    }
  };

  const getFormatIcon = (format: string) => {
    switch (format) {
      case 'comprehensive': return Book;
      case 'quick-reference': return CreditCard;
      case 'checklist': return CheckCircle;
      default: return FileText;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            className="bg-dark-800 border border-dark-600 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-dark-700">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-400 rounded-lg flex items-center justify-center">
                  <Download className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Download Safety Guides</h2>
                  <p className="text-sm text-gray-400">Generate customized emergency preparedness guides</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="flex h-[calc(90vh-120px)]">
              {/* Guide Selection */}
              <div className="w-1/2 p-6 border-r border-dark-700 overflow-y-auto">
                <h3 className="text-lg font-semibold text-white mb-4">Available Guides</h3>
                
                <div className="space-y-3">
                  {GUIDE_TEMPLATES.map((guide) => {
                    const Icon = getGuideIcon(guide.type);
                    return (
                      <motion.div
                        key={guide.id}
                        whileHover={{ scale: 1.02 }}
                        onClick={() => setSelectedGuide(guide)}
                        className={`p-4 rounded-lg border cursor-pointer transition-all ${
                          selectedGuide?.id === guide.id
                            ? 'border-primary-400 bg-primary-400/10'
                            : 'border-dark-600 hover:border-dark-500 bg-dark-700/50'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            selectedGuide?.id === guide.id ? 'bg-primary-400' : 'bg-dark-600'
                          }`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-white mb-1">{guide.title}</h4>
                            <p className="text-sm text-gray-400 mb-2">
                              {guide.type === 'general' && 'Comprehensive emergency preparedness'}
                              {guide.type === 'quick-reference' && 'Essential info in compact format'}
                              {guide.type === 'custom' && 'Personalized family emergency plan'}
                              {guide.type === 'disaster-specific' && 'Focused on specific disaster types'}
                            </p>
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <span>~{guide.estimatedPages} pages</span>
                              <span>{guide.languages.join(', ')}</span>
                              {guide.customizable && <span>Customizable</span>}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Format Options */}
                <div className="mt-6">
                  <h4 className="text-md font-semibold text-white mb-3">Format Options</h4>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      { id: 'comprehensive', name: 'Comprehensive Guide', desc: 'Full detailed guide with all information', icon: Book },
                      { id: 'quick-reference', name: 'Quick Reference', desc: 'Essential info for wallet/phone', icon: CreditCard },
                      { id: 'checklist', name: 'Checklist Format', desc: 'Step-by-step checklists only', icon: CheckCircle }
                    ].map((format) => {
                      const Icon = format.icon;
                      return (
                        <button
                          key={format.id}
                          onClick={() => setCustomization({ ...customization, format: format.id as any })}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            customization.format === format.id
                              ? 'border-primary-400 bg-primary-400/10'
                              : 'border-dark-600 hover:border-dark-500'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <Icon className="w-4 h-4 text-primary-400" />
                            <div>
                              <div className="font-medium text-white text-sm">{format.name}</div>
                              <div className="text-xs text-gray-400">{format.desc}</div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Customization Panel */}
              <div className="w-1/2 p-6 overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Customization</h3>
                  {selectedGuide?.customizable && (
                    <button
                      onClick={() => setShowCustomization(!showCustomization)}
                      className="text-primary-400 hover:text-primary-300 text-sm"
                    >
                      {showCustomization ? 'Hide Options' : 'Show Options'}
                    </button>
                  )}
                </div>

                {selectedGuide ? (
                  <div className="space-y-6">
                    {/* Preview */}
                    <div className="bg-dark-700/50 rounded-lg p-4">
                      <h4 className="font-semibold text-white mb-2">Preview: {selectedGuide.title}</h4>
                      <div className="text-sm text-gray-400 space-y-1">
                        <p>Format: {customization.format}</p>
                        <p>Language: {languages.find(l => l.code === customization.language)?.name}</p>
                        <p>Estimated pages: ~{selectedGuide.estimatedPages}</p>
                        {customization.location && <p>Location: {customization.location}</p>}
                        {customization.familyMembers.filter(m => m.trim()).length > 0 && (
                          <p>Family members: {customization.familyMembers.filter(m => m.trim()).length}</p>
                        )}
                      </div>
                    </div>

                    {/* Customization Options */}
                    {(selectedGuide.customizable || showCustomization) && (
                      <div className="space-y-4">
                        {/* Language */}
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            <Globe className="w-4 h-4 inline mr-1" />
                            Language
                          </label>
                          <select
                            value={customization.language}
                            onChange={(e) => setCustomization({ ...customization, language: e.target.value })}
                            className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                          >
                            {languages.filter(lang => selectedGuide.languages.includes(lang.code)).map(lang => (
                              <option key={lang.code} value={lang.code}>{lang.name}</option>
                            ))}
                          </select>
                        </div>

                        {/* Location */}
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            <MapPin className="w-4 h-4 inline mr-1" />
                            Your Location (Optional)
                          </label>
                          <input
                            type="text"
                            value={customization.location}
                            onChange={(e) => setCustomization({ ...customization, location: e.target.value })}
                            placeholder="City, State"
                            className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                          />
                        </div>

                        {/* Family Members */}
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            <Users className="w-4 h-4 inline mr-1" />
                            Family Members
                          </label>
                          {customization.familyMembers.map((member, index) => (
                            <div key={index} className="flex gap-2 mb-2">
                              <input
                                type="text"
                                value={member}
                                onChange={(e) => updateFamilyMember(index, e.target.value)}
                                placeholder={`Family member ${index + 1}`}
                                className="flex-1 px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                              />
                              {customization.familyMembers.length > 1 && (
                                <button
                                  onClick={() => removeFamilyMember(index)}
                                  className="px-3 py-2 bg-accent-red/20 text-accent-red rounded-lg hover:bg-accent-red/30 transition-colors"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            onClick={addFamilyMember}
                            className="text-primary-400 hover:text-primary-300 text-sm"
                          >
                            + Add family member
                          </button>
                        </div>

                        {/* Disaster Types */}
                        {selectedGuide.type === 'disaster-specific' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              <AlertTriangle className="w-4 h-4 inline mr-1" />
                              Relevant Disasters
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              {disasterTypes.map(type => (
                                <label key={type} className="flex items-center space-x-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={customization.selectedDisasters.includes(type)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setCustomization({
                                          ...customization,
                                          selectedDisasters: [...customization.selectedDisasters, type]
                                        });
                                      } else {
                                        setCustomization({
                                          ...customization,
                                          selectedDisasters: customization.selectedDisasters.filter(d => d !== type)
                                        });
                                      }
                                    }}
                                    className="w-4 h-4 text-primary-400 bg-dark-700 border-dark-600 rounded focus:ring-primary-400"
                                  />
                                  <span className="text-sm text-gray-300 capitalize">{type}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Additional Options */}
                        <div className="space-y-2">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={customization.includeContacts}
                              onChange={(e) => setCustomization({ ...customization, includeContacts: e.target.checked })}
                              className="w-4 h-4 text-primary-400 bg-dark-700 border-dark-600 rounded focus:ring-primary-400"
                            />
                            <span className="text-sm text-gray-300">Include emergency contacts</span>
                          </label>
                          
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={customization.includeLocalInfo}
                              onChange={(e) => setCustomization({ ...customization, includeLocalInfo: e.target.checked })}
                              className="w-4 h-4 text-primary-400 bg-dark-700 border-dark-600 rounded focus:ring-primary-400"
                            />
                            <span className="text-sm text-gray-300">Include local emergency information</span>
                          </label>
                        </div>
                      </div>
                    )}

                    {/* Download Button */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => generatePDF(selectedGuide)}
                      disabled={isGenerating}
                      className="w-full bg-gradient-to-r from-primary-400 to-primary-500 py-3 rounded-lg font-semibold text-white hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isGenerating ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Generating PDF...
                        </>
                      ) : (
                        <>
                          <Download className="w-5 h-5" />
                          Download Guide
                        </>
                      )}
                    </motion.button>

                    {/* Format Info */}
                    <div className="bg-dark-700/30 rounded-lg p-3">
                      <div className="flex items-center space-x-2 text-accent-green mb-2">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">Offline Ready</span>
                      </div>
                      <div className="text-xs text-gray-400 space-y-1">
                        <div className="flex items-center space-x-2">
                          <Printer className="w-3 h-3" />
                          <span>Optimized for printing</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Smartphone className="w-3 h-3" />
                          <span>Mobile-friendly format</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Zap className="w-3 h-3" />
                          <span>Works without internet</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">Select a Guide</h3>
                    <p className="text-gray-400">Choose a guide from the left to customize and download</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DownloadGuidesModal;